import { type TenantSettings, TOOL_DESCRIPTION_MAX_CHARS } from '@plato/shared';
import { useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { DataTable } from '../../components/data-table.tsx';
import { formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import type { ToolChange, ToolRow } from '../../data/rows.ts';
import { ToolServers } from './tool-servers.tsx';

const loadTools = (data: ConsoleData, tenant: TenantSummary) =>
  Promise.all([data.listTools(tenant.id), data.getIntegrationSetup(tenant.id)]);
const loadSettings = (data: ConsoleData, tenant: TenantSummary) =>
  data.getAssistantSettings(tenant.id);

type BuiltInSetting = 'webSearch' | 'webFetch' | 'scheduledTasks';

interface BuiltInTool {
  readonly setting: BuiltInSetting;
  readonly name: string;
  readonly does: string;
}

const WEB_TOOLS: readonly BuiltInTool[] = [
  {
    setting: 'webSearch',
    name: 'Web search',
    does: 'Searches the public web and cites its sources. Runs on Anthropic’s servers, on the Anthropic model route only; the search words the assistant writes are sent to the search provider.',
  },
  {
    setting: 'webFetch',
    name: 'Web fetch',
    does: 'Reads a web page or PDF whose address the user gave or a search returned. It cannot open addresses it makes up. Plato downloads the page itself; a long one is saved for the assistant’s code instead of being read in full.',
  },
];

const TASK_TOOLS: readonly BuiltInTool[] = [
  {
    setting: 'scheduledTasks',
    name: 'Scheduled tasks',
    does: 'Lets users set up tasks from the chat that run on a schedule, such as a weekly report. Each run is a new conversation with the same tools, and its credits count like a chat. While off, no task runs.',
  },
];

export function ToolsPage() {
  const { query, reload } = useReloadableTenantQuery(loadTools);
  const canEdit = useHasRole('editor');
  const canEditServers = useHasRole('admin');
  return (
    <>
      {!canEdit && <ReadOnlyNote needs="editor" />}
      <BuiltInTools
        canEdit={canEdit}
        title="Web access"
        note="Web pages come from outside your product. The sandbox where the assistant runs code stays offline either way."
        caption="Built-in tools"
        tools={WEB_TOOLS}
      />
      <BuiltInTools
        canEdit={canEdit}
        title="Scheduled tasks"
        note="Users confirm every task before it runs. Results and failures appear in the assistant, not by email."
        caption="Scheduled tasks"
        tools={TASK_TOOLS}
      />
      <Loaded query={query}>
        {([tools, setup]) => (
          <>
            {tools.length === 0 ? (
              <p className="muted">
                No tools yet. A tool appears here once the assistant has been offered it, for a
                signed-in user or a test account.
              </p>
            ) : (
              bySource(tools).map(([source, sourceTools]) => (
                <ToolSource
                  key={source}
                  source={source}
                  tools={sourceTools}
                  canEdit={canEdit}
                  onChanged={reload}
                />
              ))
            )}
            <ToolServers servers={setup.mcpServers} canEdit={canEditServers} onChanged={reload} />
          </>
        )}
      </Loaded>
    </>
  );
}

function BuiltInTools(props: {
  canEdit: boolean;
  title: string;
  note: string;
  caption: string;
  tools: readonly BuiltInTool[];
}) {
  const { data, tenant } = useConsole();
  const { query, reload } = useReloadableTenantQuery(loadSettings);
  const action = useAction();
  const change = (setting: BuiltInSetting, on: boolean) =>
    void action
      .run(async () => {
        await data.updateAssistantSettings(tenant.id, { [setting]: on });
      })
      .then((ok) => ok && reload());
  return (
    <section>
      <h2>
        {props.title} <span className="chip">Built in</span>
      </h2>
      <p className="muted">{props.note}</p>
      <Loaded query={query}>
        {(settings: TenantSettings) => (
          <DataTable
            caption={props.caption}
            columns={[
              { header: 'Tool', cell: (row) => <strong>{row.name}</strong> },
              { header: 'What it does', cell: (row) => row.does },
              {
                header: 'Status',
                cell: (row) => (
                  <span className={settings[row.setting] ? 'chip chip-success' : 'chip'}>
                    {settings[row.setting] ? 'On' : 'Off'}
                  </span>
                ),
              },
              ...(props.canEdit
                ? [
                    {
                      header: 'Actions',
                      cell: (row: BuiltInTool & { id: string }) => {
                        const on = settings[row.setting];
                        return (
                          <button
                            type="button"
                            disabled={action.pending}
                            aria-label={`${on ? 'Switch off' : 'Switch on'} ${row.name}`}
                            onClick={() => change(row.setting, !on)}
                          >
                            {on ? 'Switch off' : 'Switch on'}
                          </button>
                        );
                      },
                    },
                  ]
                : []),
            ]}
            rows={props.tools.map((tool) => ({ ...tool, id: tool.setting }))}
          />
        )}
      </Loaded>
      <ActionError error={action.error} />
    </section>
  );
}

function ToolSource(props: {
  source: string;
  tools: readonly ToolRow[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const [editing, setEditing] = useState<string | null>(null);
  const sourceEnabled = props.tools[0]?.sourceEnabled ?? true;
  const change = (tool: string, update: ToolChange) =>
    void action
      .run(() => data.updateTool(tenant.id, props.source, tool, update))
      .then((ok) => {
        if (ok) {
          setEditing(null);
          props.onChanged();
        }
      });
  const editedTool = props.tools.find((tool) => tool.name === editing);

  return (
    <section>
      <h2>
        <code>{props.source}</code> <span className="chip">MCP</span>
      </h2>
      {!sourceEnabled && (
        <p className="notice">
          This source is switched off under Tool servers below, so none of its tools are offered.
        </p>
      )}
      <DataTable
        caption={`Tools from ${props.source}`}
        columns={[
          {
            header: 'Tool',
            cell: (row) => (
              <>
                {row.title !== null && <strong>{row.title} </strong>}
                <code>{row.name}</code>
              </>
            ),
          },
          {
            header: 'What the model is told',
            cell: (row) => (
              <>
                {row.description ?? <span className="muted">Not listed yet</span>}
                {row.descriptionReplaced && <span className="chip"> Your wording</span>}
              </>
            ),
          },
          {
            header: 'Status',
            cell: (row) => (
              <span className={row.enabled ? 'chip chip-success' : 'chip'}>
                {row.enabled ? 'On' : 'Off'}
              </span>
            ),
          },
          {
            header: 'Last seen',
            cell: (row) => (row.lastSeenAt === null ? '—' : formatDate(row.lastSeenAt)),
          },
          ...(props.canEdit
            ? [
                {
                  header: 'Actions',
                  cell: (row: ToolRow & { id: string }) => (
                    <div className="row">
                      <button
                        type="button"
                        disabled={action.pending}
                        aria-label={`${row.enabled ? 'Switch off' : 'Switch on'} ${row.name}`}
                        onClick={() => change(row.name, { enabled: !row.enabled })}
                      >
                        {row.enabled ? 'Switch off' : 'Switch on'}
                      </button>
                      <button
                        type="button"
                        className="quiet"
                        aria-label={`Edit the description of ${row.name}`}
                        onClick={() => setEditing(row.name)}
                      >
                        Edit description
                      </button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        rows={props.tools.map((tool) => ({ ...tool, id: tool.name }))}
      />
      {editedTool !== undefined && (
        <DescriptionEditor
          key={editedTool.name}
          tool={editedTool}
          pending={action.pending}
          onSave={(description) => change(editedTool.name, { description })}
          onCancel={() => setEditing(null)}
        />
      )}
      <ActionError error={action.error} />
    </section>
  );
}

function DescriptionEditor(props: {
  tool: ToolRow;
  pending: boolean;
  onSave: (description: string | null) => void;
  onCancel: () => void;
}) {
  const { tool } = props;
  const [text, setText] = useState(tool.description ?? '');
  const trimmed = text.trim();
  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave(trimmed === '' || trimmed === tool.sourceDescription ? null : trimmed);
      }}
    >
      <h3>
        Description of <code>{tool.name}</code>
      </h3>
      <div>
        <span className="label">The source's own description</span>
        <p className="muted">{tool.sourceDescription ?? 'Not listed yet.'}</p>
      </div>
      <label>
        What the model is told
        <span className="hint">
          {text.length} / {TOOL_DESCRIPTION_MAX_CHARS} characters. Leave it empty to use the
          source's own.
        </span>
        <textarea
          value={text}
          rows={5}
          maxLength={TOOL_DESCRIPTION_MAX_CHARS}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="row">
        <button type="submit" className="primary" disabled={props.pending}>
          Save description
        </button>
        {tool.descriptionReplaced && (
          <button type="button" disabled={props.pending} onClick={() => props.onSave(null)}>
            Restore the source's description
          </button>
        )}
        <button type="button" className="quiet" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function bySource(tools: readonly ToolRow[]): Array<[string, ToolRow[]]> {
  const groups = new Map<string, ToolRow[]>();
  for (const tool of tools) groups.set(tool.source, [...(groups.get(tool.source) ?? []), tool]);
  return [...groups];
}
